import { useEffect, useState } from 'react';
import { useAnnounce } from './LiveRegion';

/**
 * 🔴 LOADING, WITHOUT A FLASH AND WITHOUT SILENCE — design-system plan DS-6a (5.1, 5.6; F502, F43, F78, F121, F148,
 * F177, F598).
 *
 * About a hundred screens wrote "Loading…" as bare text: it flashed on a fast answer, said nothing to a screen reader,
 * and replaced the whole screen, header included. A `Skeleton` stands where the content will be — grey lines, or a
 * table's rows and columns — and:
 * - shows nothing for the first 150ms (`useDelayedFlag`), so an answer that comes quickly never flickers;
 * - is `aria-busy` and named ("Loading invoices"), so the region says what it is waiting for;
 * - says "Still loading invoices" once in the live region at one second, so a wait is not silence.
 * The screen's heading stays above it: a skeleton replaces the body, never the page.
 */
export const SKELETON_DELAY_MS = 150;
export const STILL_LOADING_MS = 1000;

/** `on`, but only once it has stayed on for `ms` — false again as soon as it goes off. */
export function useDelayedFlag(on: boolean, ms = SKELETON_DELAY_MS): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!on) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(timer);
  }, [on, ms]);
  return on && shown;
}

export function Skeleton({
  label,
  name,
  lang,
  lines = 3,
  rows,
  cols = 4,
}: {
  /** What is loading, plural and lower-case: "invoices", "this bill". */
  label: string;
  /**
   * The whole name in the chrome's own language, when it is not English: the back office's Bangla chrome says
   * "লোড হচ্ছে…" (DS-6a review). It is also what is said at one second.
   */
  name?: string;
  lang?: string;
  /** A block of text lines (the default). */
  lines?: number;
  /** A table: this many rows of `cols` cells instead of lines. */
  rows?: number;
  cols?: number;
}) {
  const shown = useDelayedFlag(true);
  const announce = useAnnounce();
  useEffect(() => {
    // Taken back if the content arrives first: no "Still loading" after the list is on the screen (DS-6a review).
    let cancel: (() => void) | undefined;
    const timer = setTimeout(() => {
      cancel = announce(name ?? `Still loading ${label}.`);
    }, STILL_LOADING_MS);
    return () => {
      clearTimeout(timer);
      cancel?.();
    };
  }, [label, name, announce]);
  return (
    <div className="wb-skeleton" aria-busy="true" role="status" aria-label={name ?? `Loading ${label}`} lang={lang}>
      {shown
        ? rows
          ? Array.from({ length: rows }, (_, r) => (
              <div className="wb-skeleton__row" key={r}>
                {Array.from({ length: cols }, (_, c) => (
                  <span className="wb-skeleton__cell" key={c} />
                ))}
              </div>
            ))
          : Array.from({ length: lines }, (_, i) => <span className="wb-skeleton__line" key={i} />)
        : null}
    </div>
  );
}
